import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Box,
  Stack,
  Group,
  Avatar,
  Text,
  ActionIcon,
  Badge,
  Flex,
  Tooltip
} from '@mantine/core'
import {
  IoHeart,
  IoHeartOutline,
  IoShareSocialOutline,
  IoBook,
  IoChatbubbleOutline
} from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import { FEED_CARD_HOVER, FEED_ACTION_BAR_SX } from './feedCardTheme'

export interface BlogDto {
  id: string;
  userId: string;
  title: string;
  excerpt: string;
  cleverTags: string[];
  createdAt: string;
  userAvatar: string;
  userName: string;
  likeCount: number;
  thumbnail?: string;
}

interface BlogCardProps {
  blog: BlogDto;
  onLike?: (blogId: string, liked: boolean) => Promise<void>;
  onShare?: (blogId: string) => void;
}

function getRelativeTime (dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime()
  const minutes = Math.floor(diffMs / (1000 * 60))
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

export const BlogCard: React.FC<BlogCardProps> = ({ blog, onLike, onShare }) => {
  const history = useHistory()
  const { voteStore } = useStore()
  const [isLiked, setIsLiked] = useState(false)
  const [isLoadingLike, setIsLoadingLike] = useState(false)
  const [likeCount, setLikeCount] = useState(blog.likeCount)

  useEffect(() => {
    setLikeCount(blog.likeCount)
  }, [blog.id, blog.likeCount])

  const handleNavigate = () => {
    history.push(`/blog/${blog.id}`)
  }

  const handleLike = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      if (isLoadingLike) return
      setIsLoadingLike(true)
      const prevLiked = isLiked
      const prevCount = likeCount
      try {
        if (onLike) {
          const next = !isLiked
          setIsLiked(next)
          await onLike(blog.id, next)
        } else {
          if (isLiked) {
            await voteStore.removeVote(agent.VoteTargetType.Post, blog.id)
            setIsLiked(false)
            setLikeCount((c) => Math.max(0, c - 1))
          } else {
            await voteStore.castVote(agent.VoteTargetType.Post, blog.id, 1)
            setIsLiked(true)
            setLikeCount((c) => c + 1)
          }
        }
      } catch (error) {
        setIsLiked(prevLiked)
        setLikeCount(prevCount)
        console.error('Failed to like blog:', error)
      } finally {
        setIsLoadingLike(false)
      }
    },
    [blog.id, isLiked, isLoadingLike, likeCount, onLike, voteStore]
  )

  return (
    <Card
      padding={0}
      radius="md"
      withBorder
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderColor: 'var(--gs-border)',
        backgroundColor: 'var(--gs-bg-2)',
        cursor: 'pointer',
        ...FEED_CARD_HOVER
      }}
      onClick={handleNavigate}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {blog.thumbnail
          ? (
          <img
            src={blog.thumbnail}
            alt={blog.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
            )
          : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(var(--gs-accent-rgb), 0.12)'
            }}
          >
            <IoBook size={48} color="var(--gs-accent)" />
          </Box>
            )}
      </Box>

      <Box sx={{ padding: '12px 14px' }}>
        <Text size="md" fw={600} lineClamp={2} mb="xs">
          {blog.title}
        </Text>
        <Text size="sm" c="dimmed" lineClamp={2} mb="sm">
          {blog.excerpt}
        </Text>

        {blog.cleverTags && blog.cleverTags.length > 0 && (
          <Flex gap={6} mb="md" wrap="wrap" onClick={(e) => e.stopPropagation()}>
            {blog.cleverTags.slice(0, 5).map((tag) => (
              <Badge key={tag} size="sm" variant="light" color="gray">
                {tag}
              </Badge>
            ))}
            {blog.cleverTags.length > 5 && (
              <Text size="xs" c="dimmed">
                +{blog.cleverTags.length - 5}
              </Text>
            )}
          </Flex>
        )}

        <Box
          sx={{
            borderTop: '1px solid var(--gs-border)',
            paddingTop: 12
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Group spacing="sm" noWrap>
            <Avatar src={blog.userAvatar} alt={blog.userName} size={32} radius="xl" />
            <Stack spacing={2} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>
                {blog.userName}
              </Text>
              <Text size="xs" c="dimmed">
                {getRelativeTime(blog.createdAt)}
              </Text>
            </Stack>
          </Group>
        </Box>
      </Box>

      <Group
        spacing="md"
        sx={FEED_ACTION_BAR_SX}
        align="center"
        wrap="nowrap"
        onClick={(e) => e.stopPropagation()}
      >
        <ActionIcon
          variant="subtle"
          size="lg"
          color={isLiked ? 'red' : 'gray'}
          onClick={(e) => void handleLike(e)}
          disabled={isLoadingLike}
          aria-label={isLiked ? 'Remove upvote' : 'Upvote'}
        >
          {isLiked ? <IoHeart size={22} /> : <IoHeartOutline size={22} />}
        </ActionIcon>

        <Tooltip label="Comments open on the blog post" withArrow position="top">
          <ActionIcon variant="subtle" size="lg" color="gray" disabled aria-label="Comments (disabled)">
            <IoChatbubbleOutline size={22} style={{ opacity: 0.45 }} />
          </ActionIcon>
        </Tooltip>

        <ActionIcon
          variant="subtle"
          size="lg"
          color="gray"
          onClick={() => onShare?.(blog.id)}
          aria-label="Share"
          sx={{ marginLeft: 'auto' }}
        >
          <IoShareSocialOutline size={22} />
        </ActionIcon>

        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {likeCount} {likeCount === 1 ? 'like' : 'likes'}
        </Text>
      </Group>
    </Card>
  )
}

export default BlogCard
